import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const WelcomeBanner = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    activeUsers: 0,
    userGrowth: 0,
    totalUsers: 0,
    newUsersToday: 0,
    newUsersThisWeek: 0
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState('');

  const fetchUserGrowthStats = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setMessage('');
      
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      console.log('🔄 Fetching user growth stats...');
      
      const response = await fetch(`${API_URL}/api/admin/stats/user-growth`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(user?.token && { 'Authorization': `Bearer ${user.token}` })
      }
    });

      const result = await response.json();
      
      if (result.success) {
        setStats(result.data);
        if (result.message) setMessage(result.message);
        console.log('✅ User growth stats loaded:', result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch user growth stats');
      }
    } catch (err) {
      console.error('❌ Error fetching user growth stats:', err);
      // Use mock data as fallback
      const mockStats = {
        activeUsers: 892,
        userGrowth: 12.5,
        totalUsers: 1247,
        newUsersToday: 23,
        newUsersThisWeek: 156
      };
      setStats(mockStats);
      setMessage('Using demo data - ' + err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchUserGrowthStats();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(() => {
      fetchUserGrowthStats(true);
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // Format percentage with arrow indicator
  const formatGrowth = (growth) => {
    const isPositive = growth >= 0;
    const arrow = isPositive ? '↗' : '↘';
    const colorClass = isPositive ? 'text-green-600' : 'text-red-600';
    
    return (
      <span className={`flex items-center gap-1 ${colorClass}`}>
        {arrow} {Math.abs(growth).toFixed(1)}%
      </span>
    );
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl shadow-lg p-6 text-white animate-fade-in">
        <div className="flex items-center justify-between mb-4">
          <div className="space-y-2">
            <div className="h-6 w-64 bg-teal-400 rounded-lg animate-pulse"></div>
            <div className="h-4 w-48 bg-teal-400/70 rounded animate-pulse"></div>
          </div>
          <div className="h-8 w-24 bg-teal-400 rounded-lg animate-pulse"></div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((item) => (
            <div key={item} className="bg-white/20 backdrop-blur-sm rounded-xl p-4 animate-pulse">
              <div className="h-4 w-20 bg-white/40 rounded mb-2"></div>
              <div className="h-8 w-16 bg-white/40 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl shadow-lg p-6 text-white animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-xl font-bold mb-2">
            Welcome back, {user?.firstName || 'Admin'}! 👋
          </h1>
          <p className="text-teal-100 opacity-90">
            Here's what's happening with your platform users today.
          </p>
        </div>
        
        <div className="flex items-center gap-3">
                   
          <button
            onClick={() => fetchUserGrowthStats(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-teal-700 bg-white hover:bg-teal-50 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-sm backdrop-blur-sm"
          >
            <svg 
              className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Active Users */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:border-white/30 transition-all duration-300 group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-teal-100 text-sm font-medium mb-1">Active Users</p>
              <p className="text-2xl font-bold">{stats.activeUsers.toLocaleString()}</p>
              <p className="text-teal-200 text-xs mt-1">Currently online</p>
            </div>
            <div className="w-12 h-12 bg-teal-400/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>
        </div>

        {/* User Growth */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:border-white/30 transition-all duration-300 group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-teal-100 text-sm font-medium mb-1">User Growth</p>
              <p className="text-2xl font-bold">
                {formatGrowth(stats.userGrowth)}
              </p>
              <p className="text-teal-200 text-xs mt-1">This month</p>
            </div>
            <div className="w-12 h-12 bg-green-400/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
        </div>

        {/* New Today */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:border-white/30 transition-all duration-300 group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-teal-100 text-sm font-medium mb-1">New Today</p>
              <p className="text-2xl font-bold">+{stats.newUsersToday}</p>
              <p className="text-teal-200 text-xs mt-1">Registrations</p>
            </div>
            <div className="w-12 h-12 bg-blue-400/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
          </div>
        </div>

        {/* Total Users */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:border-white/30 transition-all duration-300 group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-teal-100 text-sm font-medium mb-1">Total Users</p>
              <p className="text-2xl font-bold">{stats.totalUsers.toLocaleString()}</p>
              <p className="text-teal-200 text-xs mt-1">All time</p>
            </div>
            <div className="w-12 h-12 bg-purple-400/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 mt-4 border-t border-white/20">
        <div className="flex items-center gap-2 text-xs text-teal-100 opacity-80">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {message ? 'Demo data for testing purposes' : 'Live data from production'}
        </div>
        
        <div className="flex items-center gap-1 text-xs text-teal-100 opacity-80">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Auto-refreshes every 5 minutes
        </div>
      </div>
    </div>
  );
};

export default WelcomeBanner;